'use strict';

import { CoreRepository } from '../CoreRepository';
import { UserToken } from '../../Entities/User/UserToken';
import { Utilities } from '../../Utilities/Utilities';

/**
 * Type definition for oAuth Login request
 */
export type oAuthLoginPayload = {
  username: string;
  password: string;
  client_id: string;
};

/**
 * Type definition for oAuth Login response
 */
export type oAuthLoginResponse = {
  code: string;
};

/**
 * Type definition for oAuth Login schema
 */
export type oAuthLoginSchema = {
  code: string;
};

/**
 * Type definition for oAuth get token request
 */
export type oAuthGetTokenPayload = {
  code: string;
  client_id: string;
};

/**
 * Type definition for oAuth get token response
 */
export type oAuthGetTokenResponse = {
  access_token: string;
  refresh_token: string;
  expiry: string;
};

/**
 * Type definition for oAuth get token schema
 */
export type oAuthGetTokenSchema = {
  access_token: string;
  refresh_token: string;
  expiry: Date;
};

/**
 * Type definition for oAuth get token request
 */
export type oAuthRefreshTokenPayload = {
  code: string;
  client_id: string;
};

/**
 * Type definition for oAuth get token response
 */
export type oAuthRefreshTokenResponse = {
  access_token: string;
  refresh_token: string;
  expiry: string;
};

/**
 * Type definition for oAuth get token schema
 */
export type oAuthRefreshTokenSchema = {
  access_token: string;
  refresh_token: string;
  expiry: Date;
};

export class UserTokenRepository extends CoreRepository {
  public getUserAppToken(userId: number, appId: number, options: object = null) {
    return this.webAPI.getUserAppToken(userId, appId, options).then((response) => {
      return response;
    });
  }

  /**
   * OAuth login authentication
   *
   * Authenticates a user using OAuth flow and returns auth code.
   *
   * @param payload - The OAuth login data including provider information
   * @returns Promise containing the auth code
   */
  public oAuthLogin(payload: oAuthLoginPayload): Promise<oAuthLoginSchema> {
    return this.webAPI.oAuthLogin(payload);
  }

  /**
   * OAuth login complete
   *
   * Authenticates a user using OAuth flow and returns access token.
   *
   * @param payload - The OAuth login data including provider information
   * @returns Promise containing the authentication token
   */
  public oAuthGetToken(payload: oAuthGetTokenPayload): Promise<oAuthGetTokenSchema> {
    return this.webAPI.oAuthGetToken(payload).then((response) => {
      const schema: oAuthGetTokenSchema = {
        access_token: response.access_token,
        refresh_token: response.access_token,
        expiry: Utilities.dateTimeStringToDateTime(response.expiry),
      };

      return schema;
    });
  }

  /**
   * OAuth token refresh
   *
   */
  public oAuthRefreshToken(payload: oAuthRefreshTokenPayload): Promise<oAuthRefreshTokenSchema> {
    return this.webAPI.oAuthRefreshToken(payload).then((response) => {
      const schema: oAuthRefreshTokenSchema = {
        access_token: response.access_token,
        refresh_token: response.access_token,
        expiry: Utilities.dateTimeStringToDateTime(response.expiry),
      };

      return schema;
    });
  }

  /**
   * OAuth log out
   *
   */
  public oAuthLogOut() {
    return this.webAPI.oAuthLogOut();
  }
}
